import {
  CancelDownloadCommandsResponse,
  IDownloadSessionProgressMessage,
} from '../../models/DownloadSession';
import {
  DownloadSessionCard,
  IDownloadSessionSummary,
} from '../../models/DownloadSessionCard';
import {
  DownloadSessionCounts,
  PENDING_EXPIRY_MS,
  canFulfilPlaceholder,
  cardFromSummary,
  countCards,
  isPlaceholderKey,
  isQueuedKey,
  isTerminalState,
  isWaitingCard,
  QueuedListRow,
  queuedCardKey,
  queuedListSignature,
  sameDownload,
  sortCards,
  summaryKey,
} from '../../utils/DownloadSessionSync';
import {
  CANCEL_FAILED_MESSAGE,
  CANCEL_FORBIDDEN_MESSAGE,
  CANCEL_NOT_QUEUED_YET_MESSAGE,
  cancelIdsForCard,
  cancelOutcome,
  findResultForCard,
} from '../../utils/DownloadSessionCancel';
import {
  canManageDownloads,
  isForbiddenError,
} from '../../utils/DownloadSessionAccess';
import { isSignedIn } from '../../utils/DownloadCommandsChangedRefresh';

export interface DownloadSessionState {
  sessions: Record<number, DownloadSessionCard>;
  selectedSessionId: number | null;
  panelVisible: boolean;
  /** EN-1517: GET /download-sessions/recent has answered once. */
  recentLoaded: boolean;
  /**
   * EN-1557: queuedListSignature of the Download Manager list the cards were
   * last reloaded for (null: never, or the reload failed).
   */
  queuedListSignature?: string | null;
}

let pendingIdCounter = -1;

// --------------------------------------------------------------------------
// EN-1517: whether the panel was open, kept in sessionStorage so a page
// reload reopens it only if it was open (never just because something is
// queued or running). Replaceable in unit specs (no browser there).
// --------------------------------------------------------------------------
export const PANEL_OPEN_STORAGE_KEY = 'downloadSessionPanelOpen';
/**
 * The user the flag belongs to (sessionStorage is per tab, not per user), so
 * after a sign-out and sign-in in the same tab the next user does not inherit
 * "was open".
 */
export const PANEL_OWNER_STORAGE_KEY = 'downloadSessionPanelOwner';

export type PanelOpenStorage = Pick<Storage, 'getItem' | 'setItem'>;

let panelStorage: PanelOpenStorage | null | undefined;

/** Replaces sessionStorage (unit specs); undefined restores it. */
export function setDownloadSessionPanelStorage(
  value: PanelOpenStorage | null | undefined
): void {
  panelStorage = value;
}

function getPanelStorage(): PanelOpenStorage | null {
  if (panelStorage !== undefined) return panelStorage;
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null; // storage disabled
  }
}

/**
 * True when the panel was open before this page load (or the last change).
 * With a user id, only if the flag was left by that user.
 */
export function wasPanelOpen(userId?: string | null): boolean {
  try {
    const storage = getPanelStorage();
    if (storage?.getItem(PANEL_OPEN_STORAGE_KEY) !== '1') return false;
    return (
      userId == null || storage?.getItem(PANEL_OWNER_STORAGE_KEY) === userId
    );
  } catch {
    return false;
  }
}

/**
 * Records who the flag belongs to. When the owner changes, the flag is reset
 * to closed first, so "open" never carries over to another user (not even on
 * their second reload).
 */
function setPanelOwner(userId: string | null | undefined) {
  if (userId == null) return;
  try {
    const storage = getPanelStorage();
    if (!storage) return;
    if (storage.getItem(PANEL_OWNER_STORAGE_KEY) !== userId) {
      storage.setItem(PANEL_OPEN_STORAGE_KEY, '0');
    }
    storage.setItem(PANEL_OWNER_STORAGE_KEY, userId);
  } catch {
    // Storage full or disabled: a reload just stays closed.
  }
}

/**
 * EN-1517: whether a root `login` dispatch must clear the download sessions
 * first: on sign-out (no user), or when a different user signs in. The root
 * `login` action calls this, so it works on every route, map mounted or not.
 */
export function downloadSessionsNeedClearing(
  current: { id?: string | null } | null | undefined,
  next: { id?: string | null } | null | undefined
): boolean {
  if (!next) return true;
  return current?.id != null && next.id !== current.id;
}

function setPanelVisible(state: DownloadSessionState, visible: boolean) {
  state.panelVisible = visible;
  try {
    getPanelStorage()?.setItem(PANEL_OPEN_STORAGE_KEY, visible ? '1' : '0');
  } catch {
    // Storage full or disabled: the panel still works, a reload stays closed.
  }
}

// --------------------------------------------------------------------------
// EN-1517: after a new download request, one debounced reload picks up the
// new command id and drops queued cards the request wiped on the server
// (UpdateAllUnprocessedCommandQueueToProcessedForUidAsync). Timers are
// replaceable in unit specs.
// --------------------------------------------------------------------------
export const PENDING_RELOAD_DELAY_MS = 5000;

export interface DownloadSessionTimers {
  setTimeout: (callback: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

const realTimers: DownloadSessionTimers = {
  setTimeout: (callback, ms) => setTimeout(callback, ms),
  clearTimeout: (handle) =>
    clearTimeout(handle as ReturnType<typeof setTimeout>),
};
let timers: DownloadSessionTimers = realTimers;
let pendingReload: unknown = null;
let pushReload: unknown = null;

/**
 * EN-1557: after a ReceiveDownloadCommandsChanged push, one reload of the
 * cards about 1 s later (debounced across pushes: a queued list, or a
 * queue quickly followed by the send, makes one request).
 */
export const PUSH_RELOAD_DELAY_MS = 1000;

/** Replaces setTimeout/clearTimeout (unit specs); null restores them. */
export function setDownloadSessionTimers(
  value: DownloadSessionTimers | null
): void {
  if (pendingReload != null) timers.clearTimeout(pendingReload);
  pendingReload = null;
  if (pushReload != null) timers.clearTimeout(pushReload);
  pushReload = null;
  timers = value ?? realTimers;
}

// --------------------------------------------------------------------------
// EN-1517: the WebApi calls the actions below need. Loaded lazily (the
// service imports the root store) and replaceable in unit specs.
// --------------------------------------------------------------------------
/** The WebApi's default window for GET /download-sessions/recent. */
export const DEFAULT_RECENT_HOURS = 24;

export interface DownloadSessionApi {
  getRecentDownloadSessions: (
    hours?: number,
    max?: number
  ) => Promise<IDownloadSessionSummary[]>;
  /** EN-1515 cancel (DeviceService.cancelDownloadCommands); resolves to the body. */
  cancelDownloadCommands: (
    commandIds: number[],
    downloadSessionIds: number[]
  ) => Promise<CancelDownloadCommandsResponse>;
}

let api: DownloadSessionApi | null = null;

/** Replaces the WebApi calls (unit specs); null restores the real service. */
export function setDownloadSessionApi(value: DownloadSessionApi | null): void {
  api = value;
}

async function getApi(): Promise<DownloadSessionApi> {
  if (!api) {
    api = (await import('../../services/DownloadSessionService')).default;
  }
  return api;
}

/** What a cancel click ended with (for the panel and specs). */
export type CancelClickResult =
  | 'ignored'
  | 'forbidden'
  | 'not-queued'
  | 'error'
  | 'applied';

function moveSelection(
  state: DownloadSessionState,
  from: number,
  to: number | null
) {
  if (state.selectedSessionId === from) state.selectedSessionId = to;
}

/**
 * Removes the first placeholder or queued card (other than `exceptKey`) that
 * is the same download as `identity`, and returns it.
 */
function takeMatchingWaitingCard(
  state: DownloadSessionState,
  identity: DownloadSessionCard | IDownloadSessionProgressMessage,
  replacementKey: number
): DownloadSessionCard | undefined {
  for (const key of Object.keys(state.sessions).map(Number)) {
    if (key === replacementKey) continue;
    const card = state.sessions[key];
    if (!isWaitingCard(card)) continue;
    if (!sameDownload(card, identity)) continue;
    moveSelection(state, key, replacementKey);
    delete state.sessions[key];
    return card;
  }
  return undefined;
}

export default {
  state: {
    sessions: {} as Record<number, DownloadSessionCard>,
    selectedSessionId: null as number | null,
    panelVisible: false,
    recentLoaded: false,
    queuedListSignature: null,
  } as DownloadSessionState,

  mutations: {
    CREATE_PENDING_DOWNLOAD_SESSION(
      state: DownloadSessionState,
      payload: {
        uid: number;
        fileType: number;
        startDate: string;
        endDate: string;
        message?: string;
        /** ms epoch; defaults to now (specs pass it). */
        now?: number;
        /** EN-1506 retry: the timed-out card this request replaces. */
        replacesSessionId?: number;
      }
    ) {
      if (payload.replacesSessionId != null) {
        moveSelection(state, payload.replacesSessionId, null);
        delete state.sessions[payload.replacesSessionId];
      }
      // EN-1517: older cards for this UID stay (another download for the UID
      // may still be running). Only an identical placeholder (a double
      // click) is replaced.
      for (const key of Object.keys(state.sessions).map(Number)) {
        const card = state.sessions[key];
        if (
          isPlaceholderKey(key) &&
          !card.unknown &&
          sameDownload(card, payload)
        ) {
          moveSelection(state, key, null);
          delete state.sessions[key];
        }
      }

      const tempId = pendingIdCounter--;
      state.sessions[tempId] = {
        downloadSessionId: tempId,
        uid: payload.uid,
        fileType: payload.fileType,
        state: 'T',
        percentComplete: 0,
        latestPassageTimestamp: '',
        passageCount: 0,
        startDate: payload.startDate,
        endDate: payload.endDate,
        passages: [],
        message: payload.message,
        createdAt: payload.now ?? Date.now(),
      };
      if (state.selectedSessionId === null) {
        state.selectedSessionId = tempId;
      }
      // The user asked for a download: show it.
      setPanelVisible(state, true);
    },

    UPSERT_DOWNLOAD_SESSION(
      state: DownloadSessionState,
      payload: IDownloadSessionProgressMessage
    ) {
      const id = payload.downloadSessionId;
      const existing = state.sessions[id];

      // A new real session replaces the placeholder or queued card for the
      // same download (UID, file type and range; never the UID alone), and
      // keeps its message and command id.
      let waiting: DownloadSessionCard | undefined;
      if (id > 0 && !existing) {
        waiting = takeMatchingWaitingCard(state, payload, id);
      }

      // EN-1506: the retry flag and error describe this state; a new state
      // (or a new session) starts unchecked. EN-1517: so do cancel results.
      const sameState = existing?.state === payload.state;
      state.sessions[id] = {
        downloadSessionId: id,
        uid: payload.uid,
        fileType: payload.fileType ?? 0,
        state: payload.state,
        percentComplete: payload.percentComplete ?? 0,
        latestPassageTimestamp: payload.latestPassageTimestamp ?? '',
        passageCount: payload.passageCount ?? 0,
        startDate: payload.startDate,
        endDate: payload.endDate,
        passages: existing?.passages ?? [],
        passagesLoadedState: existing?.passagesLoadedState,
        message: existing?.message ?? waiting?.message,
        retryable: sameState ? existing?.retryable : undefined,
        retryError: sameState ? existing?.retryError : undefined,
        commandId: existing?.commandId ?? waiting?.commandId,
        // A card first seen through SignalR is dated when it is first seen,
        // so the reload prune treats it like every other card.
        createdAt: existing?.createdAt ?? waiting?.createdAt ?? Date.now(),
        isIridium: existing?.isIridium ?? waiting?.isIridium,
        cancellable:
          // SignalR progress has no 'R' state (only the WebApi list does).
          payload.state === 'T' || payload.state === 'D'
            ? existing?.cancellable ?? waiting?.cancellable
            : false,
        cancelError: sameState ? existing?.cancelError : undefined,
        cancelMessage:
          existing?.cancelMessage ??
          (payload.state === 'A' ? waiting?.cancelMessage : undefined),
      };
      // Auto-select the first session when none is selected
      if (state.selectedSessionId === null) {
        state.selectedSessionId = id;
      }
      // EN-1506: a new session shows the panel. EN-1517: a session the panel
      // already has (loaded after a reload, or closed by the user) does not
      // reopen it; its card just updates. That includes a session starting
      // from a queued card or placeholder the panel already showed (a request
      // made in this browser opened the panel when it was created), and any
      // progress that arrives before the list has answered after a reload
      // (it may well be a session the list is about to return).
      if (!existing && !waiting && state.recentLoaded) {
        setPanelVisible(state, true);
      }
    },

    /**
     * EN-1517: merges GET /download-sessions/recent. Server rows are added or
     * updated, queued cards the server no longer lists are dropped (sent,
     * cancelled or superseded), and each placeholder created in this browser
     * is replaced by the server row for the same download. With `hours`,
     * finished cards the server no longer lists that are older than that
     * window are pruned, so cards do not pile up for the whole visit.
     */
    MERGE_RECENT_DOWNLOAD_SESSIONS(
      state: DownloadSessionState,
      payload:
        | IDownloadSessionSummary[]
        | { rows: IDownloadSessionSummary[]; hours?: number; now?: number }
    ) {
      const rows = Array.isArray(payload) ? payload : payload?.rows;
      const hours = Array.isArray(payload) ? undefined : payload?.hours;
      const now =
        (Array.isArray(payload) ? undefined : payload?.now) ?? Date.now();
      state.recentLoaded = true;
      const listed = new Set<number>();
      const placeholders = Object.keys(state.sessions)
        .map(Number)
        .filter((key) => isPlaceholderKey(key) && !state.sessions[key].unknown);

      for (const row of rows ?? []) {
        const key = summaryKey(row);
        listed.add(key);

        let existing = state.sessions[key];
        // A session that started from a queued card keeps that card's data.
        if (key > 0) {
          const queuedKey = queuedCardKey(row.commandId);
          const queued = state.sessions[queuedKey];
          if (queued) {
            existing = existing ?? { ...queued, downloadSessionId: key };
            moveSelection(state, queuedKey, key);
            delete state.sessions[queuedKey];
          }
        }

        const card = cardFromSummary(row, existing);

        const match = placeholders.findIndex((p) =>
          canFulfilPlaceholder(state.sessions[p], row)
        );
        if (match >= 0) {
          const placeholderKey = placeholders[match];
          const placeholder = state.sessions[placeholderKey];
          placeholders.splice(match, 1);
          card.message = card.message ?? placeholder.message;
          card.createdAt = placeholder.createdAt ?? card.createdAt;
          moveSelection(state, placeholderKey, key);
          delete state.sessions[placeholderKey];
        }

        state.sessions[key] = card;
      }

      const oldest = hours != null ? now - hours * 3600 * 1000 : null;
      for (const key of Object.keys(state.sessions).map(Number)) {
        if (listed.has(key)) continue;
        const card = state.sessions[key];
        const stale =
          oldest != null &&
          key > 0 &&
          isTerminalState(card.state) &&
          card.createdAt != null &&
          card.createdAt < oldest;
        if (isQueuedKey(key) || stale) {
          moveSelection(state, key, null);
          delete state.sessions[key];
        }
      }
      if (
        state.selectedSessionId === null ||
        !state.sessions[state.selectedSessionId]
      ) {
        state.selectedSessionId =
          sortCards(Object.values(state.sessions))[0]?.downloadSessionId ??
          null;
      }
    },

    /** EN-1517: placeholders older than PENDING_EXPIRY_MS become Unknown. */
    MARK_STALE_PLACEHOLDERS_UNKNOWN(state: DownloadSessionState, now: number) {
      for (const key of Object.keys(state.sessions).map(Number)) {
        const card = state.sessions[key];
        if (
          isPlaceholderKey(key) &&
          !card.unknown &&
          card.createdAt != null &&
          now - card.createdAt >= PENDING_EXPIRY_MS
        ) {
          card.unknown = true;
        }
      }
    },

    REMOVE_DOWNLOAD_SESSION(state: DownloadSessionState, sessionId: number) {
      delete state.sessions[sessionId];
      if (state.selectedSessionId === sessionId) {
        const ids = Object.keys(state.sessions).map(Number);
        state.selectedSessionId = ids.length > 0 ? ids[0] : null;
      }
      if (Object.keys(state.sessions).length === 0) {
        setPanelVisible(state, false);
      }
    },

    SET_DOWNLOAD_SESSION_SELECTED(
      state: DownloadSessionState,
      sessionId: number | null
    ) {
      state.selectedSessionId = sessionId;
    },

    /**
     * EN-1517: selects a card by its session id or its command id (a queued
     * or placeholder card has no session id); ids take priority, so two
     * downloads for one UID select correctly. With only a UID it selects that
     * UID's newest waiting or running card, else its newest card.
     */
    SELECT_DOWNLOAD_SESSION(
      state: DownloadSessionState,
      payload: {
        uid?: number | null;
        downloadSessionId?: number | null;
        commandId?: number | null;
      }
    ) {
      if (payload?.downloadSessionId != null) {
        if (state.sessions[payload.downloadSessionId]) {
          state.selectedSessionId = payload.downloadSessionId;
        }
        return;
      }
      if (payload?.commandId != null) {
        const card = Object.values(state.sessions).find(
          (c) => c.commandId === payload.commandId
        );
        if (card) state.selectedSessionId = card.downloadSessionId;
        return;
      }
      if (payload?.uid != null) {
        // sortCards: waiting, then running, then finished; newest first.
        const card = sortCards(
          Object.values(state.sessions).filter((c) => c.uid === payload.uid)
        )[0];
        if (card) state.selectedSessionId = card.downloadSessionId;
      }
    },

    SET_DOWNLOAD_SESSION_PANEL_VISIBLE(
      state: DownloadSessionState,
      visible: boolean
    ) {
      setPanelVisible(state, visible);
    },

    SET_QUEUED_LIST_SIGNATURE(
      state: DownloadSessionState,
      signature: string | null
    ) {
      state.queuedListSignature = signature;
    },

    CLEAR_ALL_DOWNLOAD_SESSIONS(state: DownloadSessionState) {
      state.sessions = {};
      state.selectedSessionId = null;
      // EN-1517: the next user (same tab) loads their own list.
      state.recentLoaded = false;
      state.queuedListSignature = null;
      setPanelVisible(state, false);
    },

    SET_DOWNLOAD_SESSION_RETRY_ERROR(
      state: DownloadSessionState,
      payload: { sessionId: number; message?: string }
    ) {
      const session = state.sessions[payload.sessionId];
      if (session) {
        session.retryError = payload.message;
      }
    },

    SET_DOWNLOAD_SESSION_RETRYABLE(
      state: DownloadSessionState,
      payload: { sessionId: number; retryable: boolean | undefined }
    ) {
      const session = state.sessions[payload.sessionId];
      if (session) {
        session.retryable = payload.retryable;
      }
    },

    SET_DOWNLOAD_SESSION_PASSAGES(
      state: DownloadSessionState,
      payload: {
        sessionId: number;
        passages: any[];
        loadedState?: DownloadSessionCard['state'];
      }
    ) {
      const session = state.sessions[payload.sessionId];
      if (session) {
        session.passages = payload.passages;
        session.passagesLoadedState = payload.loadedState;
      }
    },

    SET_DOWNLOAD_SESSION_CANCELLING(
      state: DownloadSessionState,
      payload: { sessionId: number; cancelling: boolean }
    ) {
      const session = state.sessions[payload.sessionId];
      if (!session) return;
      if (payload.cancelling) session.cancelling = true;
      else delete session.cancelling;
    },

    /**
     * EN-1517: applies a cancel result to a card. With a new session id (a
     * queued download the cancel turned into an 'A' session) the card moves to
     * that key, merging with a card SignalR may already have added.
     */
    APPLY_DOWNLOAD_SESSION_CANCEL(
      state: DownloadSessionState,
      payload: {
        sessionId: number;
        state?: 'A';
        message?: string;
        error?: string;
        notCancellable?: boolean;
        downloadSessionId?: number;
      }
    ) {
      const card = state.sessions[payload.sessionId];
      if (!card) {
        // SignalR already replaced the queued card with the 'A' session.
        const moved =
          payload.downloadSessionId != null
            ? state.sessions[payload.downloadSessionId]
            : undefined;
        if (moved && payload.state) {
          moved.cancelMessage = payload.message;
          moved.cancelling = false;
        }
        return;
      }
      const updated: DownloadSessionCard = {
        ...card,
        cancelError: payload.error,
        cancelling: false,
      };
      if (payload.state) {
        updated.state = payload.state;
        updated.cancellable = false;
        updated.cancelMessage = payload.message;
      }
      if (payload.notCancellable) {
        updated.cancellable = false;
      }
      const newKey = payload.downloadSessionId;
      if (newKey != null && newKey > 0 && newKey !== payload.sessionId) {
        const already = state.sessions[newKey];
        delete state.sessions[payload.sessionId];
        state.sessions[newKey] = already
          ? {
              ...already,
              commandId: already.commandId ?? card.commandId,
              message: already.message ?? card.message,
              cancelMessage: payload.message,
              cancelError: undefined,
              cancellable: false,
            }
          : { ...updated, downloadSessionId: newKey };
        moveSelection(state, payload.sessionId, newKey);
        return;
      }
      state.sessions[payload.sessionId] = updated;
    },
  },

  actions: {
    // Commits the session state and serves as the subscribeAction signal
    // for DownloadSessionPanel's debounced passage fetch.
    downloadSessionStateReceived(
      context: any,
      payload: IDownloadSessionProgressMessage
    ) {
      context.commit('UPSERT_DOWNLOAD_SESSION', payload);
    },

    createPendingDownloadSession(
      context: any,
      payload: {
        uid: number;
        fileType: number;
        startDate: string;
        endDate: string;
        message?: string;
        replacesSessionId?: number;
      }
    ) {
      context.commit('CREATE_PENDING_DOWNLOAD_SESSION', payload);
      // EN-1517: one reload about 5 s later (debounced across requests).
      if (pendingReload != null) timers.clearTimeout(pendingReload);
      pendingReload = timers.setTimeout(() => {
        pendingReload = null;
        context.dispatch('loadRecentDownloadSessions');
      }, PENDING_RELOAD_DELAY_MS);
    },

    removeDownloadSession(context: any, sessionId: number) {
      context.commit('REMOVE_DOWNLOAD_SESSION', sessionId);
    },

    /**
     * EN-1517: loads the group's recent, running and queued downloads from
     * the WebApi and merges them into the cards. It never opens the panel by
     * itself (for anyone, super admin included): it shows it when asked
     * (`show`), or reopens it when it was open before the page reload
     * (sessionStorage). Otherwise the panel stays closed and only the counts
     * show. Resolves false (and logs) when the request fails.
     */
    async loadRecentDownloadSessions(
      context: any,
      options?: { show?: boolean; hours?: number; max?: number; now?: number }
    ): Promise<boolean> {
      let rows: IDownloadSessionSummary[];
      try {
        rows = await (
          await getApi()
        ).getRecentDownloadSessions(options?.hours, options?.max);
      } catch (error) {
        console.error('Failed to load recent download sessions', error);
        return false;
      }
      context.commit('MERGE_RECENT_DOWNLOAD_SESSIONS', {
        rows: rows ?? [],
        hours: options?.hours ?? DEFAULT_RECENT_HOURS,
        now: options?.now,
      });
      const counts: DownloadSessionCounts =
        context.getters?.downloadSessionCounts ??
        countCards(Object.values(context.state.sessions));
      const userId: string | undefined = context.rootState?.user?.id;
      const show =
        options?.show || (!context.state.panelVisible && wasPanelOpen(userId));
      setPanelOwner(userId);
      if (show && counts.total > 0 && !context.state.panelVisible) {
        context.commit('SET_DOWNLOAD_SESSION_PANEL_VISIBLE', true);
      }
      return true;
    },

    /**
     * EN-1517: opens the panel (the Download Manager button, EN-1516, or the
     * counts button) and selects a card by session id, command id or UID.
     * Loads the list only if it was never loaded, or when `refresh` is true.
     */
    async openDownloadSessions(
      context: any,
      payload?: {
        uid?: number | null;
        downloadSessionId?: number | null;
        commandId?: number | null;
        refresh?: boolean;
      }
    ) {
      context.commit('SET_DOWNLOAD_SESSION_PANEL_VISIBLE', true);
      const select =
        payload &&
        (payload.uid != null ||
          payload.downloadSessionId != null ||
          payload.commandId != null)
          ? {
              uid: payload.uid,
              downloadSessionId: payload.downloadSessionId,
              commandId: payload.commandId,
            }
          : null;
      if (select) context.commit('SELECT_DOWNLOAD_SESSION', select);
      if (!context.state.recentLoaded || payload?.refresh === true) {
        await context.dispatch('loadRecentDownloadSessions', { show: true });
        if (select) context.commit('SELECT_DOWNLOAD_SESSION', select);
      }
    },

    /**
     * EN-1557: the Download Manager's queued list loaded (QueuedCommandsList,
     * the dialog's 15 s poll). There is no hub message for a queued command,
     * so when the list's queued downloads changed (queued, cancelled or sent,
     * by anyone in the group) the cards reload from the WebApi, with the same
     * group scope as the list. Resolves true when the cards were reloaded.
     */
    async queuedDownloadCommandsLoaded(
      context: any,
      rows: QueuedListRow[]
    ): Promise<boolean> {
      const signature = queuedListSignature(rows);
      if (signature === context.state.queuedListSignature) return false;
      context.commit('SET_QUEUED_LIST_SIGNATURE', signature);
      const loaded = await context.dispatch('loadRecentDownloadSessions');
      // Not loaded: forget it, so the next list load tries again.
      if (loaded === false) context.commit('SET_QUEUED_LIST_SIGNATURE', null);
      return loaded !== false;
    },

    /**
     * EN-1557: the AgmHub's ReceiveDownloadCommandsChanged push (a device's
     * queued downloads changed: queued, cancelled, range edited, retried,
     * deleted or sent). Reloads the cards from GET /download-sessions/recent
     * about 1 s later, debounced, so the collapsed card updates even when the
     * Download Manager is closed. Ignored when signed out, also if the user
     * signs out before the reload fires. A failed reload is logged by
     * loadRecentDownloadSessions; the next push or list change tries again.
     */
    downloadCommandsChangedReceived(context: any, _payload?: unknown): void {
      if (!isSignedIn(context.rootState?.user)) return;
      if (pushReload != null) timers.clearTimeout(pushReload);
      pushReload = timers.setTimeout(() => {
        pushReload = null;
        if (!isSignedIn(context.rootState?.user)) return;
        context.dispatch('loadRecentDownloadSessions');
      }, PUSH_RELOAD_DELAY_MS);
    },

    /**
     * EN-1517: when a placeholder has waited PENDING_EXPIRY_MS, asks the
     * WebApi once more (a queued command replaces it), then marks what is
     * still unmatched as Unknown.
     */
    async expirePendingDownloadSessions(context: any, now?: number) {
      const at = now ?? Date.now();
      const stale = Object.values(
        context.state.sessions as Record<number, DownloadSessionCard>
      ).some(
        (c) =>
          isPlaceholderKey(c.downloadSessionId) &&
          !c.unknown &&
          c.createdAt != null &&
          at - c.createdAt >= PENDING_EXPIRY_MS
      );
      if (!stale) return;
      const loaded = await context.dispatch('loadRecentDownloadSessions');
      // Without an answer nothing is known; try again on the next tick.
      if (loaded === false) return;
      context.commit('MARK_STALE_PLACEHOLDERS_UNKNOWN', at);
    },

    /**
     * EN-1517: the card's Cancel button, through EN-1515's
     * DeviceService.cancelDownloadCommands. Admin-only (the queueing gate); a
     * 403 shows CANCEL_FORBIDDEN_MESSAGE ("Only admins can cancel downloads.")
     * and leaves the card unchanged, and nothing is retried automatically.
     * Iridium passage downloads (ID/IDWTR) are cancelled like cellular ones.
     */
    async cancelDownloadSession(
      context: any,
      sessionId: number
    ): Promise<CancelClickResult> {
      const state: DownloadSessionState = context.state;
      let card = state.sessions[sessionId];
      if (!card || card.cancelling) return 'ignored';

      if (!canManageDownloads(context.rootState?.user)) {
        context.dispatch('notify', CANCEL_FORBIDDEN_MESSAGE);
        return 'forbidden';
      }

      let ids = cancelIdsForCard(card);
      if (!ids) {
        // A fresh placeholder has no id yet: the queued row may be listed now.
        await context.dispatch('loadRecentDownloadSessions');
        const listed = Object.values(state.sessions).find(
          (c) =>
            c.commandId != null && isWaitingCard(c) && sameDownload(c, card)
        );
        if (!listed) {
          context.dispatch('notify', CANCEL_NOT_QUEUED_YET_MESSAGE);
          return 'not-queued';
        }
        card = listed;
        sessionId = listed.downloadSessionId;
        ids = cancelIdsForCard(listed);
        if (!ids) return 'not-queued';
      }

      context.commit('SET_DOWNLOAD_SESSION_CANCELLING', {
        sessionId,
        cancelling: true,
      });
      let response: CancelDownloadCommandsResponse | undefined;
      try {
        response = await (
          await getApi()
        ).cancelDownloadCommands(ids.commandIds, ids.downloadSessionIds);
      } catch (error) {
        context.commit('SET_DOWNLOAD_SESSION_CANCELLING', {
          sessionId,
          cancelling: false,
        });
        if (isForbiddenError(error)) {
          context.dispatch('notify', CANCEL_FORBIDDEN_MESSAGE);
          return 'forbidden';
        }
        console.error('Failed to cancel download', error);
        context.commit('APPLY_DOWNLOAD_SESSION_CANCEL', {
          sessionId,
          error: CANCEL_FAILED_MESSAGE,
        });
        return 'error';
      }

      const result = findResultForCard(card, response?.results);
      const outcome = cancelOutcome(card, result);
      if (outcome.remove) {
        context.commit('REMOVE_DOWNLOAD_SESSION', sessionId);
      } else {
        context.commit('APPLY_DOWNLOAD_SESSION_CANCEL', {
          sessionId,
          state: outcome.state,
          message: outcome.message,
          error: outcome.error,
          notCancellable: outcome.notCancellable,
          downloadSessionId: outcome.downloadSessionId,
        });
      }
      if (outcome.notify) context.dispatch('notify', outcome.notify);
      if (outcome.reload) await context.dispatch('loadRecentDownloadSessions');
      return 'applied';
    },
  },

  getters: {
    activeDownloadSessionList(
      state: DownloadSessionState
    ): DownloadSessionCard[] {
      return sortCards(Object.values(state.sessions));
    },

    /** EN-1517: card counts by kind (for the panel and its opener). */
    downloadSessionCounts(state: DownloadSessionState): DownloadSessionCounts {
      return countCards(Object.values(state.sessions));
    },

    selectedDownloadSession(
      state: DownloadSessionState
    ): DownloadSessionCard | null {
      if (state.selectedSessionId === null) return null;
      return state.sessions[state.selectedSessionId] ?? null;
    },
  },
};
